import CameraControls from "camera-controls";
import { Object3D, Quaternion, Vector3 } from "three";

import {
  alphaFilterVector3,
  disableContextMenu,
} from "../../../../lib/misc/util";
import { Context } from "../../../context";
import { Actor } from "../../../world/actor/actor";

import { ControllerStrategy } from "../strategy";
import { TrackingControllerStrategy } from "./tracking";

/**
 * This class doesn't use the controller but rather just moves the camera directly.
 * For this we have to destroy the controller in init and recreate it in teardown. *
 */
export class ThirdPersonControllerStrategy extends TrackingControllerStrategy {
  protected offset: Vector3 = new Vector3(20, 3, 0);
  private cTarget: Vector3 = new Vector3();
  private cPos: Vector3 = new Vector3();
  private target: Vector3 = new Vector3();
  private pos: Vector3 = new Vector3();

  private tQ?: Quaternion;

  protected alphaFactor = 0.025 / 60;

  boneName = "joint_TorsoB";

  init(context: Context): ControllerStrategy {
    super.init(context);

    if (!this.registered) {
      console.error("no controller registered");
      return this;
    }

    if (this.registered.controls) {
      this.registered.controls.getPosition(this.pos);
      this.registered.controls.getPosition(this.cPos);
      this.registered.controls.getTarget(this.cTarget);
      this.registered.controls.getTarget(this.target);
      this.registered.controls.dispose();
      this.registered.controls = undefined;
    }

    const dom = this.registered.domRef;
    dom.addEventListener("contextmenu", disableContextMenu);

    const actor = this.findNewActor(context, this.registered.state.positionId);
    if (actor) {
      this.registered.state.tracked = this.findBone(actor);
    }

    return this;
  }

  update(deltaS: number, _gameTimeDelta: number, frameNum?: number) {
    if (!this.registered || !this.registered.state.tracked) {
      return;
    }

    if (frameNum != undefined) this.lastFrameNum = frameNum;

    const actor = this.findNewActor(
      this.registered.contextRef,
      this.registered.state.positionId
    );
    if (actor) {
      this.registered.state.tracked = this.findBone(actor);
    }

    if (!this.registered.state.tracked) {
      return;
    }

    const camera = this.registered.camera;

    this.cTarget.copy(this.registered.state.tracked.position);

    deltaS = 1 / 60;
    const alpha = this.alphaFactor / deltaS;

    alphaFilterVector3(this.target, this.cTarget, alpha);

    this.cPos.copy(this.offset);

    if (!this.tQ) {
      this.tQ = new Quaternion();
      this.tQ.copy(this.registered.state.tracked.quaternion);
    }

    this.tQ.slerp(this.registered.state.tracked.quaternion, 0.1);

    this.cPos.applyQuaternion(this.tQ);

    this.cPos.add(this.target);

    alphaFilterVector3(this.pos, this.cPos, alpha);

    camera.position.copy(this.pos);
    camera.lookAt(this.target);
    camera.updateProjectionMatrix();
  }

  teardown() {
    if (this.registered) {
      this.registered.controls = new CameraControls(
        this.registered.camera,
        this.registered.domRef
      );

      this.registered.controls.setPosition(this.pos.x, this.pos.y, this.pos.z);
      this.registered.controls.setTarget(
        this.target.x,
        this.target.y,
        this.target.z
      );
    }

    const dom = this.registered?.domRef;
    dom?.removeEventListener("contextmenu", disableContextMenu);
  }

  protected findBone(actor?: Actor): Object3D | undefined {
    if (!actor) return undefined;

    const target = actor.getObjectByName(this.boneName);
    if (target) {
      return target;
    } else {
      console.error(
        `target ${this.boneName} not found to follow for pos ${this.posId}`
      );
      return undefined;
    }
  }
}
